import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkles } from "lucide-react";
import Shell from "@/components/Shell";
import { http } from "@/lib/api";
import { useApp } from "@/lib/store";
import { track } from "@/lib/analytics";

export default function Reflection() {
  const nav = useNavigate();
  const { deviceId } = useApp();
  const [data, setData] = useState(undefined); // undefined=loading

  useEffect(() => {
    track("reflection_view");
    http.get(`/reflection/weekly/${deviceId}`)
      .then(({ data }) => setData(data))
      .catch(() => setData({ has_data: false, count: 0, needed: 4 }));
  }, [deviceId]);

  return (
    <Shell>
      <div className="min-h-[88vh] px-6 pt-14 pb-24 flex flex-col" data-testid="reflection">
        <button data-testid="reflection-back" onClick={() => nav(-1)}
          className="inline-flex items-center gap-1.5 text-[13px] text-[#6B6B6B] hover:text-[#1A1A1A] ss-focus transition-colors">
          <ArrowLeft size={16} strokeWidth={1.8} /> Back
        </button>

        <div className="mt-10">
          <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#8a6d10]">
            Your week in state
          </span>
          <h1 className="mt-2 font-serif-display text-4xl leading-tight" data-testid="reflection-title">
            What changed?
          </h1>
        </div>

        <div className="mt-8 flex-1">
          {data === undefined ? (
            <div className="ss-card p-6 animate-pulse h-28" />
          ) : data.has_data ? (
            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="ss-card p-6 border-l-2 border-[#C9A227]" data-testid="reflection-observation"
            >
              <p className="font-serif-display text-[22px] leading-snug text-[#1A1A1A]">
                {data.observation}
              </p>
              <p className="mt-4 text-[12.5px] text-[#9a9a94]">
                Based on {data.count} check-ins over the last 7 days. This is a pattern in what you
                logged — not a diagnosis.
              </p>
            </motion.div>
          ) : (
            <div className="ss-card p-6" data-testid="reflection-learning">
              <div className="flex items-center gap-2">
                <Sparkles size={16} strokeWidth={1.8} className="text-[#C9A227]" />
                <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#8a6d10]">
                  We're still learning your pattern
                </span>
              </div>
              <p className="mt-3 text-[15px] leading-relaxed text-[#474747]">
                A few more check-ins will help Signal State identify recurring changes across your week.
                {data.count > 0 && ` You've logged ${data.count} of ${data.needed || 4} this week so far.`}
              </p>
            </div>
          )}
        </div>

        <button
          data-testid="reflection-checkin"
          onClick={() => nav("/navigate")}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#1A1A1A] text-[#FDFBF7] rounded-full px-6 py-4 text-base font-medium ss-tap ss-focus hover:-translate-y-0.5"
        >
          Check my state now <ArrowRight size={18} strokeWidth={1.8} />
        </button>
      </div>
    </Shell>
  );
}
